import * as React from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getMyAdminStatus } from "@/lib/billing.functions";
import { adminGrantCreditsForHarness } from "@/lib/billing.functions";
import {
  harnessCompare,
  harnessCrashResume,
  harnessListRuns,
  harnessPreviewCost,
  harnessSeedRun,
  harnessTimings,
  type HarnessRunSummary,
} from "@/lib/harness.functions";

// Cache-bust marker: bump when we need to force a new route chunk hash so
// clients cannot execute a stale server-fn hash that no longer exists in
// the deployed manifest.
const HARNESS_CLIENT_BUILD = "2026-07-04T21:30:00Z-serialfix-1";

export const Route = createFileRoute("/_authed/admin/harness")({
  head: () => ({
    meta: [
      { title: "Admin: Harness" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: async () => {
    if (typeof window === "undefined") return;
    const status = await getMyAdminStatus();
    if (!status.isAdmin) throw redirect({ to: "/dashboard" });
  },
  component: HarnessPage,
});

type SpawnedRun = {
  id: string;
  label: string;
  createdAt: number;
};

function fmtMs(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms)) return "-";
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = s / 60;
  return `${m.toFixed(1)}m`;
}

function HarnessPage() {
  const preview = useServerFn(harnessPreviewCost);
  const grant = useServerFn(adminGrantCreditsForHarness);
  React.useEffect(() => {
    // Temporary diagnostic: log the exact server-fn URLs (hash-suffixed)
    // the browser will POST to. Compare against the deployed manifest.
    // eslint-disable-next-line no-console
    console.log("[harness-debug] build:", HARNESS_CLIENT_BUILD, {
      harnessPreviewCostUrl: (harnessPreviewCost as unknown as { url?: string }).url,
      adminGrantCreditsForHarnessUrl: (adminGrantCreditsForHarness as unknown as { url?: string }).url,
    });
  }, []);
  const seed = useServerFn(harnessSeedRun);
  const crash = useServerFn(harnessCrashResume);
  const compare = useServerFn(harnessCompare);
  const timings = useServerFn(harnessTimings);
  const listRuns = useServerFn(harnessListRuns);

  const [spawned, setSpawned] = React.useState<SpawnedRun[]>([]);

  const listQ = useQuery({
    queryKey: ["harnessListRuns"],
    queryFn: () => listRuns(),
  });

  // Session status strip: poll each spawned run for status + timings.
  const [statusMap, setStatusMap] = React.useState<
    Record<string, { status: string; current_stage: number | null; processing_ms: number | null }>
  >({});
  React.useEffect(() => {
    if (spawned.length === 0) return;
    let cancelled = false;
    const tick = async () => {
      const next: typeof statusMap = { ...statusMap };
      for (const run of spawned) {
        try {
          const t = await timings({ data: { runId: run.id } });
          next[run.id] = {
            status: t.status,
            current_stage: null,
            processing_ms: t.processing_wall_clock_ms,
          };
        } catch {
          // ignore transient errors
        }
      }
      if (!cancelled) setStatusMap(next);
    };
    void tick();
    const id = setInterval(tick, 5_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
     
  }, [spawned.map((s) => s.id).join(","), timings]);

  return (
    <div className="mx-auto max-w-6xl space-y-8 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Pipeline regression harness</h1>
        <p className="text-sm text-muted-foreground">
          Admin only. Reads completed runs, seeds controlled re-runs, and
          compares outputs. Does not modify pipeline behavior.
        </p>
      </div>

      {/* Status strip */}
      {spawned.length > 0 && (
        <section className="rounded-md border p-4">
          <h2 className="mb-2 text-sm font-semibold">Runs spawned this session</h2>
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1 pr-4">Label</th>
                <th className="py-1 pr-4">Run ID</th>
                <th className="py-1 pr-4">Status</th>
                <th className="py-1 pr-4">Processing wall clock</th>
              </tr>
            </thead>
            <tbody>
              {spawned.map((r) => {
                const s = statusMap[r.id];
                return (
                  <tr key={r.id} className="border-t">
                    <td className="py-1 pr-4">{r.label}</td>
                    <td className="py-1 pr-4 font-mono text-xs">{r.id}</td>
                    <td className="py-1 pr-4">{s?.status ?? "polling..."}</td>
                    <td className="py-1 pr-4">
                      {s?.status === "completed" ? fmtMs(s.processing_ms) : "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      <SeedPanel
        title="Seed a re-run from a completed source"
        allowCrash={false}
        onSpawn={async ({ sourceRunId, fromStage, spawnN, crashAfterBatch }) => {
          const cost = await preview({ data: { sourceRunId, fromStage } });
          const confirmed = window.confirm(
            `Grant ${cost.bufferPerSpawn} credits per spawn.\n` +
              `Total buffer: ${cost.bufferPerSpawn} x ${spawnN} = ${cost.bufferPerSpawn * spawnN}\n` +
              `Team credits currently: ${cost.teamCredits}\n\n` +
              `Proceed?`,
          );
          if (!confirmed) return;
          const reason = `harness_seed:${sourceRunId}`;
          await grant({
            data: {
              teamId: cost.teamId,
              amount: cost.bufferPerSpawn * spawnN,
              reason,
            },
          });
          const spawnedIds: SpawnedRun[] = [];
          for (let i = 0; i < spawnN; i += 1) {
            const res = await seed({ data: { sourceRunId, fromStage } });
            spawnedIds.push({
              id: res.runId,
              label: `seed[${i + 1}/${spawnN}] fromStage=${fromStage}`,
              createdAt: Date.now(),
            });
          }
          setSpawned((prev) => [...spawnedIds, ...prev]);
          void crashAfterBatch;
        }}
      />

      <SeedPanel
        title="Crash-resume: pre-populate a mid-stage checkpoint"
        allowCrash
        onSpawn={async ({ sourceRunId, fromStage, spawnN, crashAfterBatch }) => {
          if (crashAfterBatch === null) return;
          const cost = await preview({ data: { sourceRunId, fromStage } });
          const confirmed = window.confirm(
            `Crash-resume: batches before ${crashAfterBatch} are treated as done.\n` +
              `Grant ${cost.bufferPerSpawn} credits per spawn (loose upper bound).\n` +
              `Total buffer: ${cost.bufferPerSpawn * spawnN}\n\nProceed?`,
          );
          if (!confirmed) return;
          const reason = `harness_crash_resume:${sourceRunId}`;
          await grant({
            data: {
              teamId: cost.teamId,
              amount: cost.bufferPerSpawn * spawnN,
              reason,
            },
          });
          const spawnedIds: SpawnedRun[] = [];
          for (let i = 0; i < spawnN; i += 1) {
            const res = await crash({
              data: { sourceRunId, fromStage, crashAfterBatch },
            });
            if ("skipped" in res) {
              window.alert(`Skipped: ${res.reason}`);
              continue;
            }
            spawnedIds.push({
              id: res.runId,
              label: `crash[${i + 1}/${spawnN}] fromStage=${fromStage} after=${crashAfterBatch}`,
              createdAt: Date.now(),
            });
          }
          setSpawned((prev) => [...spawnedIds, ...prev]);
        }}
      />

      <ComparePanel comparer={compare} runs={listQ.data ?? []} />
    </div>
  );
}

type SpawnArgs = {
  sourceRunId: string;
  fromStage: 2 | 3;
  spawnN: number;
  crashAfterBatch: number | null;
};

function SeedPanel({
  title,
  allowCrash,
  onSpawn,
}: {
  title: string;
  allowCrash: boolean;
  onSpawn: (args: SpawnArgs) => Promise<void>;
}) {
  const [sourceRunId, setSourceRunId] = React.useState("");
  const [fromStage, setFromStage] = React.useState<2 | 3>(2);
  const [spawnN, setSpawnN] = React.useState(1);
  const [crashAfterBatch, setCrashAfterBatch] = React.useState<number | null>(1);

  const submit = useMutation({
    mutationFn: async () => {
      await onSpawn({ sourceRunId, fromStage, spawnN, crashAfterBatch });
    },
  });

  return (
    <section className="space-y-3 rounded-md border p-4">
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <div className="md:col-span-2">
          <Label>Source run ID (completed)</Label>
          <Input
            value={sourceRunId}
            onChange={(e) => setSourceRunId(e.target.value.trim())}
            placeholder="uuid"
          />
        </div>
        <div>
          <Label>From stage</Label>
          <select
            value={fromStage}
            onChange={(e) => setFromStage(Number(e.target.value) as 2 | 3)}
            className="w-full rounded-md border bg-background px-2 py-2 text-sm"
          >
            <option value={2}>2 (contact finder)</option>
            <option value={3}>3 (outreach)</option>
          </select>
        </div>
        <div>
          <Label>Spawn count</Label>
          <Input
            type="number"
            min={1}
            max={10}
            value={spawnN}
            onChange={(e) => setSpawnN(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
          />
        </div>
        {allowCrash && (
          <div>
            <Label>Crash after batch (1-based)</Label>
            <Input
              type="number"
              min={1}
              value={crashAfterBatch ?? 1}
              onChange={(e) => setCrashAfterBatch(Math.max(1, Number(e.target.value) || 1))}
            />
          </div>
        )}
      </div>
      <Button
        disabled={!sourceRunId || submit.isPending}
        onClick={() => submit.mutate()}
      >
        {submit.isPending ? "Working..." : "Preview cost and spawn"}
      </Button>
      {submit.error && (
        <p className="text-sm text-red-600">
          {(submit.error as Error).message ?? String(submit.error)}
        </p>
      )}
    </section>
  );
}

function ComparePanel({
  comparer,
  runs,
}: {
  comparer: ReturnType<typeof useServerFn<typeof harnessCompare>>;
  runs: HarnessRunSummary[];
}) {
  const [ids, setIds] = React.useState("");
  const [result, setResult] = React.useState<unknown>(null);
  const run = useMutation({
    mutationFn: async () => {
      const runIds = ids
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const r = await comparer({ data: { runIds } });
      setResult(r);
    },
  });

  return (
    <section className="space-y-3 rounded-md border p-4">
      <h2 className="text-lg font-semibold">Compare runs</h2>
      <Textarea
        value={ids}
        onChange={(e) => setIds(e.target.value)}
        placeholder="Paste run IDs separated by newlines or commas (>= 2)."
        rows={4}
      />
      {runs.length > 0 && (
        <div className="text-xs text-muted-foreground">
          Recent harness runs (click to add):{" "}
          {runs.slice(0, 20).map((r) => (
            <button
              key={r.id}
              type="button"
              className="mr-2 underline"
              onClick={() =>
                setIds((prev) => (prev ? prev + "\n" + r.id : r.id))
              }
            >
              {r.harness_label}:{r.id.slice(0, 8)}
            </button>
          ))}
        </div>
      )}
      <Button disabled={run.isPending} onClick={() => run.mutate()}>
        {run.isPending ? "Comparing..." : "Compare"}
      </Button>
      {run.error && (
        <p className="text-sm text-red-600">
          {(run.error as Error).message ?? String(run.error)}
        </p>
      )}
      {result !== null && (
        <pre className="max-h-[600px] overflow-auto rounded bg-muted p-3 text-xs">
          {JSON.stringify(result, null, 2)}
        </pre>
      )}
    </section>
  );
}